<template>
  <div>
    <!-- 悬浮搜索按钮 -->
    <button
      ref="searchTrigger"
      type="button"
      class="floating-search-btn"
      @click="handleSearchClick"
      :title="tooltip"
      :aria-label="tooltip"
      :aria-expanded="showSearchModal ? 'true' : 'false'"
      aria-controls="article-search-dialog">
      <i class="el-icon-search" aria-hidden="true"></i>
    </button>

    <!-- 搜索模态框 -->
    <div
      v-if="showSearchModal"
      class="search-modal-overlay"
      @click="closeSearchModal"
      @keydown="handleModalKeydown">
      <section
        id="article-search-dialog"
        ref="searchModal"
        class="search-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-dialog-title"
        @click.stop>
        <div class="search-modal-header">
          <h2 id="search-dialog-title">搜索文章</h2>
          <button type="button" class="close-btn" @click="closeSearchModal" aria-label="关闭搜索">
            <i class="el-icon-close" aria-hidden="true"></i>
          </button>
        </div>

        <div class="search-modal-content">
          <div class="search-input-container">
            <el-input
              v-model="searchKeyword"
              placeholder="输入关键词搜索文章..."
              clearable
              @keyup.enter.native="handleSearch"
              @clear="handleClearSearch"
              ref="searchInput">
              <el-button
                slot="append"
                icon="el-icon-search"
                @click="handleSearch"
                aria-label="提交搜索"
                :loading="isSearching">
              </el-button>
            </el-input>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FloatingSearch',
  props: {
    // 按钮提示文本
    tooltip: {
      type: String,
      default: '搜索文章'
    },
    // 是否正在搜索
    isSearching: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showSearchModal: false,
      searchKeyword: ''
    }
  },
  methods: {
    // 搜索按钮点击
    handleSearchClick() {
      this.showSearchModal = true
      this.$nextTick(() => {
        this.$refs.searchInput && this.$refs.searchInput.focus()
      })
    },

    // 关闭搜索模态框
    closeSearchModal() {
      this.showSearchModal = false
      this.$nextTick(() => {
        this.$refs.searchTrigger && this.$refs.searchTrigger.focus()
      })
    },

    handleModalKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeSearchModal()
        return
      }

      if (event.key !== 'Tab' || !this.$refs.searchModal) return

      const focusable = Array.from(
        this.$refs.searchModal.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])')
      ).filter(element => !element.disabled && element.offsetParent !== null)

      if (focusable.length === 0) return

      const firstElement = focusable[0]
      const lastElement = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    },

    // 执行搜索
    handleSearch() {
      if (!this.searchKeyword.trim()) {
        this.$message.warning('请输入搜索关键词')
        return
      }
      this.closeSearchModal()
      this.$emit('search', this.searchKeyword.trim())
    },

    // 清空搜索输入
    handleClearSearch() {
      this.searchKeyword = ''
    }
  }
}
</script>

<style scoped>
/* 悬浮搜索按钮样式 */
.floating-search-btn {
  position: fixed;
  right: 2rem;
  bottom: 2rem;
  width: 52px;
  height: 52px;
  padding: 0;
  border: none;
  background: #6b5f4c;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(107, 95, 76, 0.24);
  transition: transform 0.2s ease, background-color 0.2s ease;
  color: #f5f0e8;
  font-size: 1.5rem;
  z-index: 1000;
}

.floating-search-btn:hover,
.floating-search-btn:focus-visible {
  transform: translateY(-2px);
  background: #594f40;
  outline: none;
}

.floating-search-btn i {
  transition: transform 0.3s ease;
}

.floating-search-btn:hover i,
.floating-search-btn:focus-visible i {
  transform: scale(1.1);
}

/* 搜索模态框样式 */
.search-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  padding: 1rem;
}

.search-modal {
  background: var(--card-bg);
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.search-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid var(--border-color);
}

.search-modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-color);
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.1);
  color: var(--text-color);
}

.search-modal-content {
  padding: 1.5rem 2rem 2rem;
}

.search-input-container {
  margin-bottom: 2rem;
}

.search-input-container .el-input__inner {
  border-radius: 12px;
  border: 2px solid var(--border-color);
  background: var(--bg-color);
  font-size: 1rem;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
}

.search-input-container .el-input__inner:focus {
  border-color: #6b5f4c;
  box-shadow: 0 0 0 3px rgba(107, 95, 76, 0.1);
}

.search-input-container .el-button {
  background: #6b5f4c;
  border-color: #6b5f4c;
  color: #f5f0e8;
  border-radius: 0 12px 12px 0;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
}

.search-input-container .el-button:hover {
  background: #8b7d6b;
  border-color: #8b7d6b;
  transform: translateY(-1px);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .floating-search-btn {
    right: 1rem;
    bottom: 1rem;
    width: 48px;
    height: 48px;
  }

  .search-modal {
    width: 100%;
  }

  .search-modal-header,
  .search-modal-content {
    padding: 1rem 1.5rem;
  }
}
</style>
